import type { TelemetryQueue, QueueEntry } from './queue';
import type { SendResult } from './types';

/**
 * Envoi de la file au Worker (A79, A80) : **jamais bloquant, jamais bruyant**.
 *
 * `shouldSend` (pure) décide si un envoi peut partir ; `flushQueue` poste les
 * lots, retire de la file ce que le serveur a accepté (2xx) et s'arrête à la
 * première erreur, sans nouvelle tentative avant le prochain déclencheur.
 */

/** Un lot : 10 enregistrements ou ≈ 300 Ko au plus (1 Mo avant la v0.55.6 : trop long à partir en 5G faible). */
export const BATCH_MAX_RECORDS = 10;
export const BATCH_MAX_BYTES = 300_000;
/** Délai d'une requête avant abandon : base, plus une seconde par tranche de `SEND_BYTES_PER_SECOND` octets (v0.55.6). */
export const SEND_TIMEOUT_MS = 8000;
/** Débit montant plancher supposé (20 Ko/s ≈ 160 kbit/s : 4G faible, 5G en bordure de cellule). */
export const SEND_BYTES_PER_SECOND = 20_000;
/** Délai maximal, quelle que soit la taille du lot. */
export const SEND_TIMEOUT_MAX_MS = 60_000;

/** Délai accordé à un lot selon sa taille : 8 s + 1 s par 20 Ko, 60 s au plus (un lot de 300 Ko : 23 s). */
export function sendTimeoutMs(bytes: number, base: number = SEND_TIMEOUT_MS): number {
  const extra = Math.ceil(Math.max(0, bytes) / SEND_BYTES_PER_SECOND) * 1000;
  return Math.min(SEND_TIMEOUT_MAX_MS, base + extra);
}

/**
 * Un échec **passager** (v0.55.6) : délai dépassé, réseau, 5xx ou erreur interne. L'installateur
 * relance alors de lui-même après un délai croissant ; les refus définitifs (400, 413) et les
 * erreurs de configuration (401, 403, 404) attendent le prochain déclencheur, comme avant.
 */
export function isTransientResult(result: SendResult): boolean {
  if (result === 'error:timeout' || result === 'error:network' || result === 'error:internal') return true;
  const m = /^error:(\d{3})$/.exec(result);
  return m !== null && Number(m[1]) >= 500;
}
/** `keepalive` n'est demandé que sous cette taille de corps (limite des navigateurs : 64 Ko). */
export const KEEPALIVE_MAX_BYTES = 60 * 1024;
/** Surcharge d'URL en mode debug (champ de la section Debug « Télémétrie »). */
export const URL_OVERRIDE_KEY = 'diceordie.telemetry.url';
/** Chemin d'envoi côté Worker. */
export const REPORTS_PATH = '/v1/reports';

// ─────────────────────────────────────────────────────────────────── Décision

export interface SendContext {
  consent: boolean;
  /** URL du Worker, `null` si aucune n'est configurée. */
  url: string | null;
  /** `navigator.onLine !== false`. */
  online: boolean;
  /** Phase du jeu : jamais d'envoi pendant une Expédition ou une Invasion. */
  phase: string;
  /** Un envoi est déjà en cours. */
  sending: boolean;
}

export type SendRefusal = 'disabled' | 'noUrl' | 'offline' | 'inCombat' | 'busy';

export type SendDecision = { ok: true } | { ok: false; reason: SendRefusal };

/** Conditions d'un envoi : consentement, URL, réseau, hors combat, pas d'envoi en cours. */
export function shouldSend(ctx: SendContext): SendDecision {
  if (!ctx.consent) return { ok: false, reason: 'disabled' };
  if (!ctx.url) return { ok: false, reason: 'noUrl' };
  if (!ctx.online) return { ok: false, reason: 'offline' };
  if (ctx.phase === 'expedition' || ctx.phase === 'invasion') return { ok: false, reason: 'inCombat' };
  if (ctx.sending) return { ok: false, reason: 'busy' };
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────── URL

/**
 * URL d'envoi à partir de l'adresse du Worker : accepte la racine
 * (`https://x.workers.dev`) ou l'adresse complète (`…/v1/reports`). `null` si
 * vide ou si ce n'est pas une URL http(s).
 */
export function reportsUrl(base: string | null | undefined): string | null {
  const trimmed = (base ?? '').trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    const path = u.pathname.replace(/\/+$/, '');
    u.pathname = path.endsWith(REPORTS_PATH) ? path : path + REPORTS_PATH;
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Racine du Worker (`https://hôte`) à partir de l'adresse d'envoi des rapports
 * (`https://hôte/v1/reports`) ; `null` sans adresse (A112 : les clients des codes
 * et des événements du mur parlent à d'autres routes du même Worker).
 */
export function workerBase(reports: string | null): string | null {
  if (!reports) return null;
  try {
    const u = new URL(reports);
    const path = u.pathname.replace(/\/+$/, '');
    u.pathname = path.endsWith(REPORTS_PATH) ? path.slice(0, -REPORTS_PATH.length) : path;
    u.search = '';
    u.hash = '';
    return u.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * URL effective : la surcharge Debug (mode debug seulement) l'emporte sur la
 * variable de build `VITE_TELEMETRY_URL`.
 */
export function effectiveUrl(opts: { buildUrl?: string | undefined; override?: string | null; debug: boolean }): string | null {
  if (opts.debug) {
    const o = reportsUrl(opts.override);
    if (o) return o;
  }
  return reportsUrl(opts.buildUrl);
}

// ──────────────────────────────────────────────────────────────────── Envoi

/** Champs ajoutés par le schéma 2 (A87) : retirés pour parler à un Worker ou une base encore en schéma 1. */
export const SCHEMA2_FIELDS = ['saveId', 'tainted', 'balanceVersion', 'bestThrow'] as const;

/**
 * Un enregistrement de schéma 2 réécrit en schéma 1 (`schema: 1`, sans les quatre
 * champs des classements). La file garde l'enregistrement complet ; seul l'envoi
 * est dépouillé. Un enregistrement illisible est renvoyé tel quel.
 */
export function toSchema1(json: string): string {
  try {
    const rec = JSON.parse(json) as Record<string, unknown>;
    if (rec.schema !== 2) return json;
    rec.schema = 1;
    for (const k of SCHEMA2_FIELDS) delete rec[k];
    return JSON.stringify(rec);
  } catch {
    return json;
  }
}

/** Corps JSON d'un lot, composé sans relire les enregistrements (sauf `downgrade` : schéma 1). */
export function composeBody(installId: string, sentAt: string, entries: QueueEntry[], downgrade = false): string {
  return `{"installId":${JSON.stringify(installId)},"sentAt":${JSON.stringify(sentAt)},"records":[${entries
    .map((e) => (downgrade ? toSchema1(e.json) : e.json))
    .join(',')}]}`;
}

/**
 * Schéma d'envoi de la session (repli A87) : 2 tant que le Worker et la base le
 * tiennent, 1 sinon. Un Worker d'avant la phase 7 refuse un enregistrement de
 * schéma 2 en 400 (`records[i].schema : schéma 1 attendu`), et ce 400 est
 * définitif : sans repli la file serait vidée.
 */
export interface SchemaGate {
  schema: 1 | 2;
}

/** Un 400 dont la raison nomme `schema` ou l'un des quatre champs du schéma 2. */
export function isSchemaRefusal(detail: string | undefined): boolean {
  return !!detail && /schema|schéma|saveId|tainted|balanceVersion|bestThrow/.test(detail);
}

/** Délai de la sonde de santé (courte : elle précède le premier envoi, jamais bloquante). */
export const HEALTH_TIMEOUT_MS = 4000;
export const HEALTH_PATH = '/v1/health';

/** Adresse de `GET /v1/health` à partir de l'adresse d'envoi. */
export function healthUrl(reports: string | null): string | null {
  if (!reports) return null;
  try {
    const u = new URL(reports);
    const path = u.pathname.replace(/\/+$/, '');
    u.pathname = (path.endsWith(REPORTS_PATH) ? path.slice(0, -REPORTS_PATH.length) : path) + HEALTH_PATH;
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return null;
  }
}

export type HealthFetch = (
  url: string,
  init: { method: 'GET'; signal: AbortSignal; credentials: 'omit' },
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/**
 * Sonde `GET /v1/health` : `{ schema, answered }`. `schema` vaut 2 seulement si le
 * Worker répond `schema: 2` ; tout le reste (ancien Worker qui répond 1, base en
 * schéma 1, réseau, délai, réponse illisible) vaut 1. `answered` : le Worker a répondu
 * (sinon on re-sondera au prochain envoi). Ne lève jamais.
 */
export async function probeSchema(
  reports: string | null,
  fetchFn: HealthFetch,
  timeoutMs: number = HEALTH_TIMEOUT_MS,
): Promise<{ schema: 1 | 2; answered: boolean }> {
  const url = healthUrl(reports);
  if (!url) return { schema: 1, answered: false };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await Promise.race([
      fetchFn(url, { method: 'GET', signal: ctrl.signal, credentials: 'omit' }),
      new Promise<never>((_, reject) => ctrl.signal.addEventListener('abort', () => reject(new Error('délai')))),
    ]);
    let schema: 1 | 2 = 1;
    try {
      const data = (await res.json()) as { schema?: unknown } | null;
      // Un Worker en schéma 2 **ou plus récent** (3 depuis A112 : codes et événements du
      // mur) parle le schéma 2 des enregistrements de combat.
      if (res.ok && data && typeof data.schema === 'number' && data.schema >= 2) schema = 2;
    } catch {
      schema = 1;
    }
    return { schema, answered: true };
  } catch {
    return { schema: 1, answered: false };
  } finally {
    clearTimeout(timer);
  }
}

export type FetchLike = (
  url: string,
  init: {
    method: 'POST';
    headers: Record<string, string>;
    body: string;
    signal: AbortSignal;
    credentials: 'omit';
    keepalive?: boolean;
  },
) => Promise<{ ok: boolean; status: number; text?: () => Promise<string> }>;

/** Longueur gardée du corps d'une réponse d'erreur (raison d'un 400, affichée en Debug). */
export const ERROR_DETAIL_MAX = 300;

/**
 * Raison lisible d'une réponse d'erreur : le champ `error` du JSON du Worker
 * (`{"error":"records[0].enemies : …"}`), sinon le début du corps brut.
 */
export function errorDetail(body: string): string | undefined {
  const raw = body.trim();
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && typeof (parsed as { error?: unknown }).error === 'string') {
      return ((parsed as { error: string }).error.slice(0, ERROR_DETAIL_MAX)) || undefined;
    }
  } catch {
    /* pas du JSON : corps brut */
  }
  return raw.slice(0, ERROR_DETAIL_MAX);
}

/** Indice `i` du `records[i]` que nomme la raison d'un 400, s'il est dans le lot. */
export function rejectedIndex(detail: string | undefined, batchLength: number): number | null {
  const m = detail ? /records\[(\d+)\]/.exec(detail) : null;
  if (!m) return null;
  const i = Number(m[1]);
  return Number.isInteger(i) && i >= 0 && i < batchLength ? i : null;
}

/**
 * Statuts **définitifs** : le serveur a lu l'envoi et le refuse pour ce qu'il
 * contient (400 schéma refusé, 413 trop gros). Le renvoyer tel quel échouerait
 * toujours et bloquerait la file (A79) : le lot est retiré et compté « rejeté ».
 * 401 / 403 / 404 / 5xx / réseau : la file est gardée, on réessaiera.
 */
export function isDefinitiveRejection(status: number): boolean {
  return status === 400 || status === 413;
}

export interface FlushDeps {
  queue: TelemetryQueue;
  /** Adresse complète d'envoi (`reportsUrl`). */
  url: string;
  installId: string;
  /** Clé partagée facultative (`x-dod-key`). */
  key?: string;
  fetchFn: FetchLike;
  now?: () => Date;
  /** Délai fixe (tests) ; sinon `sendTimeoutMs` selon la taille du lot. */
  timeoutMs?: number;
  /** Schéma d'envoi de la session (repli A87) ; sans lui, les lots partent tels que la file les garde. */
  gate?: SchemaGate;
  /** Interrogé avant chaque lot suivant : `false` interrompt l'envoi (un combat vient de commencer…). */
  canContinue?: () => boolean;
}

export interface FlushOutcome {
  result: SendResult;
  /** Enregistrements acceptés par le serveur pendant cet envoi. */
  sent: number;
  /** Enregistrements retirés de la file parce que le serveur les refuse définitivement (400, 413). */
  rejected: number;
  /** Début du corps de la dernière réponse d'erreur (la raison d'un 400), sinon absent. */
  detail?: string;
}

/** Poste la file lot par lot ; s'arrête à la première erreur. Ne lève jamais. */
export async function flushQueue(deps: FlushDeps): Promise<FlushOutcome> {
  let sent = 0;
  let rejected = 0;
  let lastRejection: { result: SendResult; detail?: string } | null = null;
  const done = (): FlushOutcome =>
    lastRejection
      ? { result: lastRejection.result, sent, rejected, ...(lastRejection.detail ? { detail: lastRejection.detail } : {}) }
      : { result: 'ok', sent, rejected };
  try {
    await deps.queue.idle();
    // La file est bornée (60 enregistrements) : la boucle l'est aussi.
    for (let guard = 0; guard < 100; guard++) {
      if (guard > 0 && deps.canContinue && !deps.canContinue()) return { result: 'inCombat', sent, rejected };
      const batch = deps.queue.peek(BATCH_MAX_RECORDS, BATCH_MAX_BYTES);
      if (batch.length === 0) return done();
      const sentAt = (deps.now ? deps.now() : new Date()).toISOString();
      const body = composeBody(deps.installId, sentAt, batch, deps.gate?.schema === 1);
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (deps.key) headers['x-dod-key'] = deps.key;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), deps.timeoutMs ?? sendTimeoutMs(body.length));
      let status: number;
      let detail: string | undefined;
      try {
        const res = await deps.fetchFn(deps.url, {
          method: 'POST',
          headers,
          body,
          signal: ctrl.signal,
          credentials: 'omit',
          ...(body.length < KEEPALIVE_MAX_BYTES ? { keepalive: true } : {}),
        });
        status = res.ok ? 200 : res.status;
        if (!res.ok && res.text) {
          try {
            detail = errorDetail(await res.text());
          } catch {
            detail = undefined;
          }
        }
      } catch (err) {
        const aborted = ctrl.signal.aborted || (err as { name?: string } | null)?.name === 'AbortError';
        return { result: aborted ? 'error:timeout' : 'error:network', sent, rejected };
      } finally {
        clearTimeout(timer);
      }
      // Repli A87 : un 400 qui nomme le schéma ou un champ des classements vient d'un Worker
      // d'avant la phase 7 : on passe en schéma 1 pour la session et on renvoie le même lot.
      if (status === 400 && deps.gate && deps.gate.schema === 2 && isSchemaRefusal(detail)) {
        deps.gate.schema = 1;
        continue;
      }
      if (isDefinitiveRejection(status)) {
        // Lot refusé pour son contenu : il ne passera jamais tel quel. Un 400
        // nomme l'enregistrement fautif (`records[i]…`) : on ne retire que
        // lui et les autres repartent au tour suivant ; sinon (413, raison
        // illisible) tout le lot est retiré. On continue dans les deux cas.
        const culprit = status === 400 ? rejectedIndex(detail, batch.length) : null;
        const toDrop = culprit === null ? batch : [batch[culprit]];
        await deps.queue.remove(toDrop.map((e) => e.seq));
        rejected += toDrop.length;
        lastRejection = { result: `error:${status}`, detail };
        continue;
      }
      if (status < 200 || status >= 300) return { result: `error:${status}`, sent, rejected, ...(detail ? { detail } : {}) };
      await deps.queue.remove(batch.map((e) => e.seq));
      sent += batch.length;
    }
    return done();
  } catch {
    return { result: 'error:internal', sent, rejected };
  }
}
